import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { OverviewSectionCard } from '@/components/agent/overview/OverviewSectionCard';
import { OverviewSettingRow } from '@/components/agent/overview/OverviewSettingRow';
import { ActivationPackWithTarget } from '@/types/activation/activation-pack';
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface PackOverviewSectionProps {
  // View mode props
  pack?: ActivationPackWithTarget | null;
  // Edit mode props
  isEditing?: boolean;
  onEditToggle?: () => void;
  // Form state (for both create and edit)
  name: string;
  description: string;
  targetType: 'genie' | 'portal';
  targetId: string;
  onNameChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onTargetTypeChange: (value: 'genie' | 'portal') => void;
  onTargetIdChange: (value: string) => void;
  // Target options
  agents: { id: string; name: string }[];
  portals: { id: string; name: string }[];
  // Styling
  brand?: string;
  // Mode
  isCreateMode?: boolean;
}

export const PackOverviewSection = ({
  pack,
  isEditing = false,
  onEditToggle,
  name,
  description,
  targetType,
  targetId,
  onNameChange,
  onDescriptionChange,
  onTargetTypeChange,
  onTargetIdChange,
  agents,
  portals,
  brand = '#4E9CFF',
  isCreateMode = false,
}: PackOverviewSectionProps) => {
  const showEditForm = isEditing || isCreateMode;

  const getTargetName = () => {
    if (pack?.agent) return pack.agent.name;
    if (pack?.portal) return pack.portal.name;
    if (targetType === 'genie') {
      return agents.find((a) => a.id === targetId)?.name || 'Select a genie';
    }
    return portals.find((p) => p.id === targetId)?.name || 'Select a portal';
  };

  const getTargetType = () => {
    if (pack?.agent) return 'genie';
    if (pack?.portal) return 'portal';
    return targetType;
  };

  return (
    <OverviewSectionCard
      title={isCreateMode ? 'Target & Details' : 'Pack Overview'}
      subtitle={isCreateMode ? 'Select what this pack will activate' : 'Basic information about this activation pack'}
      genieIcon="play"
      brand={brand}
      onEdit={!isCreateMode ? onEditToggle : undefined}
    >
      {showEditForm ? (
        <div className="space-y-6">
          {/* Target Type Selection (only in create mode or if editing allows target change) */}
          {isCreateMode && (
            <>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">What would you like to activate?</label>
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant={targetType === 'genie' ? 'default' : 'outline'}
                    className="flex-1"
                    onClick={() => {
                      onTargetTypeChange('genie');
                      onTargetIdChange('');
                    }}
                  >
                    Genie
                  </Button>
                  <Button
                    type="button"
                    variant={targetType === 'portal' ? 'default' : 'outline'}
                    className="flex-1"
                    onClick={() => {
                      onTargetTypeChange('portal');
                      onTargetIdChange('');
                    }}
                  >
                    Portal
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Select {targetType === 'genie' ? 'Genie' : 'Portal'}
                </label>
                <Select value={targetId} onValueChange={onTargetIdChange}>
                  <SelectTrigger>
                    <SelectValue placeholder={`Choose a ${targetType}...`} />
                  </SelectTrigger>
                  <SelectContent>
                    {targetType === 'genie'
                      ? agents.map((agent) => (
                          <SelectItem key={agent.id} value={agent.id}>
                            {agent.name}
                          </SelectItem>
                        ))
                      : portals.map((portal) => (
                          <SelectItem key={portal.id} value={portal.id}>
                            {portal.name}
                          </SelectItem>
                        ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2 sm:col-span-2">
              <label className="text-sm font-medium text-gray-700">Pack Name *</label>
              <Input
                value={name}
                onChange={(e) => onNameChange(e.target.value)}
                placeholder="e.g., Main Marketing Materials"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Description</label>
            <Textarea
              value={description}
              onChange={(e) => onDescriptionChange(e.target.value)}
              placeholder="What is this pack for?"
              rows={2}
            />
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <OverviewSettingRow
            label="Pack Name"
            value={<span className="font-semibold">{pack?.name || name}</span>}
          />
          <OverviewSettingRow
            label="Target"
            value={
              <span className="flex items-center gap-2">
                <GenieIcon name={getTargetType() === 'genie' ? 'genie-lamp' : 'web-page'} size={20} />
                {getTargetName()}
              </span>
            }
          />
          {(pack?.description || description) && (
            <OverviewSettingRow
              label="Description"
              value={pack?.description || description}
              fullWidth
            />
          )}
        </div>
      )}
    </OverviewSectionCard>
  );
};
